import { useCallback, useEffect, useRef } from "react";
import { EDITING_PRESENCE_IDLE_MS } from "@vida/wave-core";

const THROTTLE_MS = 2_000;
const CURSOR_THROTTLE_MS = 200;

export type PublishPresenceFn = (
  anchor: string | null,
  cursorOffset: number | null,
  cursorEndOffset: number | null,
) => void;

export function useWaveEditingPresence(onPublishPresence: PublishPresenceFn) {
  const editingBlockRef = useRef<string | null>(null);
  const presenceActiveRef = useRef(false);
  const lastPublishRef = useRef(0);
  const lastCursorRef = useRef<{ offset: number; endOffset: number } | null>(
    null,
  );
  const cursorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCursorRef = useRef<{
    blockId: string;
    offset: number;
    endOffset: number;
  } | null>(null);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearEditingSignal = useCallback(() => {
    if (!presenceActiveRef.current) return;
    presenceActiveRef.current = false;
    onPublishPresence(null, null, null);
  }, [onPublishPresence]);

  const scheduleIdleClear = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      if (editingBlockRef.current == null) return;
      clearEditingSignal();
    }, EDITING_PRESENCE_IDLE_MS);
  }, [clearIdleTimer, clearEditingSignal]);

  const publishAnchor = useCallback(
    (
      blockId: string,
      force = false,
      cursorOffset: number | null = null,
      cursorEndOffset: number | null = null,
    ) => {
      const now = Date.now();
      if (
        !force &&
        blockId === editingBlockRef.current &&
        cursorOffset == null &&
        now - lastPublishRef.current < THROTTLE_MS
      ) {
        return;
      }
      lastPublishRef.current = now;
      presenceActiveRef.current = true;
      onPublishPresence(blockId, cursorOffset, cursorEndOffset);
    },
    [onPublishPresence],
  );

  const flushCursor = useCallback(() => {
    const pending = pendingCursorRef.current;
    if (!pending) return;
    pendingCursorRef.current = null;
    const { blockId, offset, endOffset } = pending;
    if (editingBlockRef.current !== blockId || !presenceActiveRef.current) {
      return;
    }
    if (
      lastCursorRef.current?.offset === offset &&
      lastCursorRef.current?.endOffset === endOffset
    ) {
      return;
    }
    lastCursorRef.current = { offset, endOffset };
    publishAnchor(blockId, true, offset, endOffset);
  }, [publishAnchor]);

  const publishCursor = useCallback(
    (blockId: string, offset: number, endOffset: number) => {
      if (editingBlockRef.current !== blockId || !presenceActiveRef.current) {
        return;
      }
      pendingCursorRef.current = { blockId, offset, endOffset };
      if (cursorTimerRef.current) return;
      cursorTimerRef.current = setTimeout(() => {
        cursorTimerRef.current = null;
        flushCursor();
      }, CURSOR_THROTTLE_MS);
    },
    [flushCursor],
  );

  const beginEditing = useCallback(
    (blockId: string) => {
      if (editingBlockRef.current !== blockId) {
        editingBlockRef.current = blockId;
        lastCursorRef.current = null;
      }
      publishAnchor(blockId, true, null, null);
      scheduleIdleClear();
    },
    [publishAnchor, scheduleIdleClear],
  );

  const touchTypingActivity = useCallback(
    (blockId: string) => {
      if (editingBlockRef.current !== blockId) {
        editingBlockRef.current = blockId;
        lastCursorRef.current = null;
      }
      const cursor = lastCursorRef.current;
      publishAnchor(
        blockId,
        true,
        cursor?.offset ?? null,
        cursor?.endOffset ?? null,
      );
      scheduleIdleClear();
    },
    [publishAnchor, scheduleIdleClear],
  );

  const endEditing = useCallback(() => {
    if (editingBlockRef.current == null) return;
    clearIdleTimer();
    if (cursorTimerRef.current) {
      clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = null;
    }
    pendingCursorRef.current = null;
    editingBlockRef.current = null;
    lastPublishRef.current = 0;
    lastCursorRef.current = null;
    presenceActiveRef.current = false;
    onPublishPresence(null, null, null);
  }, [clearIdleTimer, onPublishPresence]);

  useEffect(
    () => () => {
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      clearIdleTimer();
      endEditing();
    },
    [clearIdleTimer, endEditing],
  );

  return { beginEditing, endEditing, publishCursor, touchTypingActivity };
}
